<script>
    import {fade} from 'svelte/transition';

    import * as Utils from '@js/modules/utils';
    import I18n from '@js/modules/translations';
    import {activateOnKey} from '@js/modules/a11y';
    import StorageService from '@js/modules/storage';

    import {clickOutside} from '@js/components/themes/actions/clickOutside';

    import MapRequest from '@js/modules/maps/mapRequest';

    import Icon from '@js/components/themes/Icon.svelte';
    import Bounce from '@js/components/themes/Bounce.svelte';

    import closeIcon from '@js/components/icons/closeIcon';
    import searchIcon from '@js/components/icons/searchIcon';

    import {
        DefaultRideType,
        AutocompleteLimit,
        MaxSearchRate
    } from '@js/components/modules/constants';

    import {RIDE_TYPE_ICON, PLACE_ICON} from '@js/components/themes/icons';

    const RECENT_SEARCH_STORAGE_NAME = 'combo_search_recent_search';
    const RECENT_SEARCH_MAX = 5;

    const uniqRecentSearches = function (recents) {
        return recents.reduce((current, next) => {
            if (!current.some(a => a.uniqueId === next.uniqueId)) {
                current.push(next);
            }
            return current;
        }, []);
    };

    let {
        id,
        formId,
        title = I18n.t('js.combo_search.title'),
        placeholder,
        searchButton = I18n.t('js.helpers.buttons.search'),
        isFullWidth = false,
        defaultRideType = window.currentUserRideType || DefaultRideType,
        initialValue,
        searchValue = $bindable({value: ''}),
        minQueryLength = 2,
        useFirstQueryOnEnter = true,
        useRecentSearch = false,
        useSubmitOnSelect = false,
        recentStorageName = RECENT_SEARCH_STORAGE_NAME,
        recentTitle = I18n.t('js.combo_search.recent.title'),
        useRideType = true,
        // Hide the ride-type selector on mobile (used when an on-map route-type bar already
        // provides it, to avoid duplicating the control).
        rideTypeHiddenOnMobile = false,
        onRideTypeChange,
        onSubmit
    } = $props();

    let searchInput = $state();
    let isSearchFetching = $state(false);
    let searchResults = $state([]);
    let searchHighlightedIndex = $state(null);
    let isSearchOpened = $state(false);
    let location = $state({});
    let useMyPosition = $state(false);
    let selectOnMap = $state(false);
    let isRideTypeOpened = $state(false);
    let rideType = $state(defaultRideType);
    let recentSearches = $state(useRecentSearch ? uniqRecentSearches(StorageService.getItem(recentStorageName) || []) : []);

    let _locationRequest;
    const _locationRequestAbort = {};
    let enterPressed = false;

    if (initialValue) {
        searchValue.value = initialValue;
    }

    let _proximity = {
        lat: parseFloat(window.userLatitude) || 0,
        lng: parseFloat(window.userLongitude) || 0,
        // Proximity bias radius around the user's real position: 10 keeps nearby places first while
        // staying wide enough to surface mid/long-range targets (~300-800 km) over exact namesakes
        // abroad. Higher (12+) is too tight and lets a far exact match outrank the intended place.
        zoom: 10
    };
    if (!_proximity.lat) {
        const currentCountry = window.supportedCountries.find((country) => country.code === window.countryCode);

        _proximity = {
            lat: currentCountry.map_center[0],
            lng: currentCountry.map_center[1],
            // Broad bias: this is the country centroid, not the user's actual location.
            zoom: 7
        };
    }

    const handelSubmitSearch = () => {
        if (useRecentSearch && location.value) {
            let previousRecentSearches = StorageService.getItem(recentStorageName) || [];
            previousRecentSearches.unshift($state.snapshot(location));

            previousRecentSearches = uniqRecentSearches(previousRecentSearches);

            if (previousRecentSearches.length > RECENT_SEARCH_MAX) {
                previousRecentSearches.length = RECENT_SEARCH_MAX;
            }

            StorageService.setItem(recentStorageName, previousRecentSearches);

            recentSearches = previousRecentSearches;
        }

        onSubmit({
            location: $state.snapshot(location),
            useMyPosition,
            selectOnMap,
            rideType
        });
    };

    const setSearchValue = (selectedLocation) => {
        location = selectedLocation || {};
        useMyPosition = false;
        selectOnMap = false;
        isSearchOpened = false;

        isSearchFetching = false;
        enterPressed = false;

        searchValue = {value: selectedLocation?.fullName || searchValue.value};

        if (useSubmitOnSelect) {
            handelSubmitSearch();
        }
    };

    const _onLocationResult = (features) => {
        let newResults = [];

        if (features?.length) {
            newResults = newResults.concat(
                features.map((featureLocation) => ({
                    type: 'location',
                    uniqueId: `location-${featureLocation.fullName.replace(/ /g, '_')}`,
                    // osmId: featureLocation.osmId,
                    placeType: featureLocation.placeType,
                    value: featureLocation.fullName,
                    name: featureLocation.name,
                    fullName: featureLocation.fullName,
                    placeName: featureLocation.placeName,
                    center: featureLocation.center,
                    bbox: featureLocation.bbox
                }))
            );
        }

        return newResults;
    };

    const _autocomplete = Utils.debounce((query, proximity) => {
        if (query.length < minQueryLength) {
            return;
        }

        const requests = [];

        if (_locationRequestAbort?.signal) {
            _locationRequestAbort.signal.abort();
        }

        _locationRequest = MapRequest.locationSearch(query, {
            locale: I18n.locale,
            proximity: proximity,
            limit: AutocompleteLimit,
            autocomplete: true,
            sortByPlaceType: true,
            sortByMatchingName: true,
            sortByLocaleCountry: true
        }, {
            abort: _locationRequestAbort
        });

        requests.push(_locationRequest);

        Promise.allSettled(requests)
            .then((results) => {
                isSearchFetching = false;

                let newResults = [];

                results.forEach((result /*, promiseIndex */) => {
                    if (result.status === 'fulfilled' && result.value) {
                        newResults = newResults.concat(_onLocationResult(result.value));
                    }
                });

                searchResults = newResults;

                if (enterPressed) {
                    enterPressed = false;

                    setSearchValue(searchResults[searchHighlightedIndex]);

                    isSearchOpened = false;
                    searchHighlightedIndex = null;
                }
            });
    }, MaxSearchRate);

    const handleSearchFocusIn = () => {
        if ((location.type || useMyPosition || selectOnMap) && isSearchOpened) {
            return;
        }

        isSearchOpened = true;

        if (useMyPosition || selectOnMap) {
            useMyPosition = false;
            selectOnMap = false;

            searchValue = {value: ''};
        }
    };

    const handleSearchFocusOut = (event) => {
        if (isSearchOpened && event.target.id === id) {
            return;
        }

        isSearchOpened = false;
    };

    const handleSearchInput = () => {
        useMyPosition = false;
        selectOnMap = false;
        isSearchFetching = true;

        if (searchValue) {
            _autocomplete(searchValue.value, _proximity);
        } else {
            searchResults = [];
            searchHighlightedIndex = null;
        }
    };

    const handleSearchKeydown = (event) => {
        enterPressed = false;

        if (event.key === 'ArrowDown') {
            if (searchHighlightedIndex < searchResults.length - 1) {
                searchHighlightedIndex === null ? searchHighlightedIndex = 0 : searchHighlightedIndex += 1;
            } else {
                searchHighlightedIndex = 0;
            }
        } else if (event.key === 'ArrowUp' && searchHighlightedIndex !== null) {
            searchHighlightedIndex === 0 ? searchHighlightedIndex = searchResults.length - 1 : searchHighlightedIndex -= 1;
        } else if (event.key === 'Enter') {
            event.preventDefault();

            enterPressed = true;

            // Clear focus of input
            event.target.blur();

            // If enter press a second times and there are results, submit the query
            if ((location.type || useMyPosition || selectOnMap) && !isSearchOpened) {
                handelSubmitSearch();
            }

            // Wait fetching is finish to select results
            if (!isSearchFetching) {
                if (useFirstQueryOnEnter && searchHighlightedIndex === null) {
                    searchHighlightedIndex = 0;
                }

                setSearchValue(searchResults[searchHighlightedIndex]);

                searchHighlightedIndex = null;

                isSearchOpened = false;

                _locationRequestAbort.signal?.abort();
            }
        } else if (event.key === 'Escape') {
            isSearchFetching = false;
            searchHighlightedIndex = null;

            isSearchOpened = false;

            _locationRequestAbort.signal?.abort();
        } else if (!searchValue.value && isSearchFetching) {
            isSearchFetching = false;
        }
    };

    const highlightQuery = (currentValue) => {
        let newValue = currentValue;

        const replaceIndexes = [];
        try {
            currentValue.normalize('NFD')
                .trim()
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/[\s-_+@#$%^&*,;]/g, ' ')
                .replace(new RegExp(searchValue.value, 'gi'), (match, startIndex) => {
                    replaceIndexes.push({
                        startIndex,
                        match
                    });
                });
        } catch {
            // Do nothing
        }

        if (replaceIndexes.length) {
            replaceIndexes.forEach((indexData) => {
                let replacedValue = currentValue.substring(indexData.startIndex, indexData.startIndex + indexData.match.length);
                if (replacedValue.charAt(0)
                    .toUpperCase() === replacedValue.charAt(0)) {
                    replacedValue = replacedValue.charAt(0)
                        .toUpperCase() + replacedValue.slice(1);
                }
                newValue = currentValue.replaceAt(indexData.startIndex, indexData.match.length, `<strong>${replacedValue}</strong>`);
            });
        }

        return newValue;
    };

    const clearSearchInput = (event) => {
        event.preventDefault();

        isSearchFetching = false;
        searchValue = {value: ''};
        searchResults = [];

        requestAnimationFrame(() => {
            searchInput.focus();
            isSearchOpened = true;
        });
    };

    const handleSearchOutsideClick = () => {
        isSearchOpened = false;
    };

    const handleRideTypeClick = () => {
        isRideTypeOpened = true;
    };

    const handleRideTypeChange = (routeType, event) => {
        event.preventDefault();

        rideType = routeType;

        isRideTypeOpened = false;

        if (onRideTypeChange) {
            onRideTypeChange(routeType);
        }
    };

    const handleRideTypeOutside = () => {
        isRideTypeOpened = false;
    };

    const handleLocateMe = (event) => {
        event.preventDefault();

        isSearchOpened = false;

        searchValue = {value: I18n.t('js.combo_search.position.placeholder')};

        useMyPosition = true;
        selectOnMap = false;

        if (useSubmitOnSelect) {
            handelSubmitSearch();
        }
    };

    const handleSelectOnMap = (event) => {
        event.preventDefault();

        isSearchOpened = false;

        searchValue = {value: I18n.t('js.combo_search.map.placeholder')};

        selectOnMap = true;
        useMyPosition = false;

        if (useSubmitOnSelect) {
            handelSubmitSearch();
        }
    };

    const handleSubmitClick = (event) => {
        event.preventDefault();

        enterPressed = true;

        if (!isSearchOpened && searchResults.length) {
            location = searchResults[0];
        }

        handelSubmitSearch();
    };

    $effect(() => {
        rideType = defaultRideType;
    });

    $effect(() => {
        return () => {
            _locationRequestAbort.signal?.abort();
        };
    });
</script>

{#snippet renderSubmitButton()}
    <!-- The visible label lives in the md:block span below, so on a phone the button
         is icon-only: without this it has no accessible name at all. -->
    <button type="submit"
            aria-label={searchButton}
            class="ml-2 md:ml-4 px-4 py-2 text-white font-medium text-base bg-primary-700 rounded-full cursor-pointer hover:bg-primary-800 focus:outline-hidden focus:ring-2 focus:ring-primary-300"
            onclick={handleSubmitClick}>
        <span class="block md:hidden">
            <Icon class="w-5 h-5"
                  icon={searchIcon}/>
        </span>

        <span class="hidden md:block">
            {searchButton}
        </span>
    </button>
{/snippet}

<form id={formId}
      class="search"
      accept-charset="UTF-8"
      autocomplete="off"
      novalidate="novalidate"
      onsubmit={handleSubmitClick}>
    <div class="relative w-[94%] md:w-auto max-w-4xl mx-auto bg-white rounded-xl md:rounded-full block-shadow-xl"
         class:w-full={isFullWidth}>
        <div class="flex flex-row items-center justify-around md:justify-between flex-wrap gap-4 md:gap-0 h-auto md:h-[84px] px-4 md:px-6 py-3 md:py-0">
            <div class="relative flex items-center justify-between flex-nowrap grow basis-full md:basis-auto">
                <div class="relative grow text-left z-[1048]">
                    <label for={id}
                           class="block w-full mb-1 text-sm md:text-xs font-bold text-gray-900 cursor-pointer">
                        {title}
                    </label>

                    <input type="search"
                           id={id}
                           class="block w-full p-1 pl-0 text-base md:text-sm text-gray-900 border-t-0 border-l-0 border-r-0 border-b-1 md:border-0 bg-white text-ellipsis overflow-hidden whitespace-nowrap cursor-pointer focus:border-b-1 focus:border-black focus:rounded-none focus:ring-0 focus:outline-hidden focus:cursor-text"
                           placeholder={placeholder}
                           required
                           autocomplete="off"
                           autocorrect="off"
                           autocapitalize="off"
                           spellcheck="false"
                           onfocusin={handleSearchFocusIn}
                           onfocusout={handleSearchFocusOut}
                           onkeydown={handleSearchKeydown}
                           oninput={handleSearchInput}
                           bind:this={searchInput}
                           bind:value={searchValue.value}>

                    <div class="absolute end-1 bottom-0.5 md:bottom-1.5 flex items-center justify-center pr-2 pb-1 md:pb-0 bg-white z-[1049]">
                        {#if isSearchFetching}
                            <Bounce color="black"/>
                        {/if}

                        {#if searchValue.value && !isSearchFetching}
                            <!-- svelte-ignore a11y_missing_attribute -->
                            <a class="ml-1.5 md:ml-2 cursor-pointer"
                               role="button"
                               tabindex="0"
                               aria-label={I18n.t('js.a11y.clear_search')}
                               onclick={clearSearchInput}
                               onkeydown={activateOnKey(clearSearchInput)}>
                                <Icon class="w-5 h-5"
                                      icon={closeIcon}/>
                            </a>
                        {/if}
                    </div>
                </div>

                <div class="block md:hidden"
                     class:hidden={isSearchOpened}>
                    {@render renderSubmitButton()}
                </div>

                {#if isSearchOpened}
                    <div {@attach clickOutside(handleSearchOutsideClick, id)}
                         transition:fade={{ duration: 300 }}
                         class="absolute top-[-10px] left-[-40px] right-[-10px] ms-7 ml-4 mt-px p-2 bg-white rounded-md block-shadow z-[1047]">
                        <div class="min-h-36 pt-[64px]">
                            {#if searchResults.length > 0}
                                <ul>
                                    {#each searchResults as searchResult, i (searchResult.uniqueId)}
                                        <li class="group list-none p-1.5 text-left text-sm rounded-md cursor-pointer hover:bg-gray-200"
                                            class:bg-gray-200={i === searchHighlightedIndex}
                                            role="option"
                                            aria-selected={i === searchHighlightedIndex}
                                            tabindex="0"
                                            onclick={() => setSearchValue(searchResult)}
                                            onkeydown={activateOnKey(() => setSearchValue(searchResult))}>
                                            <div class="flex flex-row items-center justify-start">
                                                <div class="mr-3">
                                                    <Icon class="text-black shrink-0 w-4 h-4"
                                                          icon={PLACE_ICON[searchResult.placeType]}/>
                                                </div>
                                                <div class="flex flex-col">
                                                    <span class="text-sm">
                                                        {@html highlightQuery(searchResult.name)}
                                                    </span>
                                                    <span class="text-xs text-gray-500">
                                                        {searchResult.placeName}
                                                    </span>
                                                </div>
                                            </div>
                                        </li>
                                    {/each}
                                </ul>
                            {:else}
                                <div class="flex items-center justify-start">
                                    <p class="mt-1 text-xs text-gray-600 italic">
                                        {I18n.t('js.combo_search.helper')}
                                    </p>
                                </div>
                            {/if}

                            {#if recentSearches.length > 0}
                                <div class="my-4 text-left">
                                    <p class="ml-1 my-1 text-sm text-gray-600">
                                        {recentTitle}
                                    </p>

                                    <ul>
                                        {#each recentSearches as recentSearch (recentSearch.uniqueId)}
                                            <li class="group list-none p-1.5 text-sm rounded-md cursor-pointer hover:bg-gray-200"
                                                role="option"
                                                aria-selected="false"
                                                tabindex="0"
                                                onclick={() => setSearchValue(recentSearch)}
                                                onkeydown={activateOnKey(() => setSearchValue(recentSearch))}>
                                                <div class="flex flex-row items-center justify-start">
                                                    <div class="mr-3">
                                                        <Icon class="text-black shrink-0 w-4 h-4"
                                                              icon={PLACE_ICON[recentSearch.placeType]}/>
                                                    </div>
                                                    <div class="flex flex-col">
                                                        <span class="text-sm">
                                                            {@html highlightQuery(recentSearch.name)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </li>
                                        {/each}
                                    </ul>
                                </div>
                            {/if}
                        </div>

                        <div class="flex items-center justify-start gap-6 pt-2">
                            <button type="button"
                                    class="flex justify-between items-center me-2 mb-2 px-4 py-2.5 text-white text-xs font-bold bg-black rounded-lg cursor-pointer focus:ring-2 focus:ring-gray-300 focus:outline-hidden hover:bg-gray-800"
                                    onclick={handleLocateMe}>
                                <svg style="height: 22px; width: 22px; margin-right: 8px;"
                                     viewBox="0 0 24 24">
                                    <path fill="currentColor"
                                          d="M21 3L3 10.53V11.5L9.84 14.16L12.5 21H13.46L21 3Z"
                                          style="fill: currentcolor;">
                                    </path>
                                </svg>
                                {I18n.t('js.combo_search.position.button')}
                            </button>

                            <button type="button"
                                    class="flex justify-between items-center me-2 mb-2 px-4 py-2.5 text-white text-xs font-bold bg-black rounded-lg cursor-pointer focus:ring-2 focus:ring-gray-300 focus:outline-hidden hover:bg-gray-800"
                                    onclick={handleSelectOnMap}>
                                <svg style="height: 22px; width: 22px; margin-right: 8px;"
                                     viewBox="0 0 24 24">
                                    <title>map</title>
                                    <path d="M15,19L9,16.89V5L15,7.11M20.5,3C20.44,3 20.39,3 20.34,3L15,5.1L9,3L3.36,4.9C3.15,4.97 3,5.15 3,5.38V20.5A0.5,0.5 0 0,0 3.5,21C3.55,21 3.61,21 3.66,20.97L9,18.9L15,21L20.64,19.1C20.85,19 21,18.85 21,18.62V3.5A0.5,0.5 0 0,0 20.5,3Z"
                                          style="fill: currentcolor;">
                                    </path>
                                </svg>
                                {I18n.t('js.combo_search.map.button')}
                            </button>
                        </div>
                    </div>
                {/if}
            </div>

            {#if useRideType}
                <div class="hidden md:block h-[46px] mx-0 md:mx-6 border-r-1"></div>

                <div class="flex flex-nowrap justify-around w-full md:w-auto gap-2 md:gap-0">
                    {#if useRideType}
                        <div class="relative min-w-28 text-center {rideTypeHiddenOnMobile ? 'hidden md:block' : ''}">
                            <div class="relative w-full z-[1046]">
                                <label for="dropdown-ride-type"
                                       class="block w-full mb-1 px-2 text-sm md:text-xs font-bold text-gray-900 cursor-pointer">
                                    {I18n.t('js.combo_search.rides.select')}
                                </label>

                                <button id="dropdown-ride-type"
                                        class="inline-flex items-center justify-center w-full px-2 py-1.5 text-center text-black text-sm font-medium bg-white cursor-pointer hover:bg-gray-100 focus:ring-0 focus:outline-hidden rounded-lg"
                                        type="button"
                                        onclick={handleRideTypeClick}>
                                    {I18n.t(`js.ride.enums.ride_type.${rideType}`)}
                                </button>
                            </div>

                            {#if isRideTypeOpened}
                                <div {@attach clickOutside(handleRideTypeOutside, 'dropdown-ride-type')}
                                     transition:fade={{ duration: 300 }}
                                     id="dropdown-ride-type-container"
                                     class="absolute top-[-10px] left-[-40px] right-[-16px] ms-7 ml-4 mt-px p-0 divide-y divide-gray-100 bg-white rounded-md block-shadow cursor-pointer z-[1045]">
                                    <ul class="mt-[78px] p-0 space-y-1 text-sm text-gray-700 border-t-1 border-black"
                                        aria-labelledby="dropdown-ride-type">
                                        {#each Object.keys(RIDE_TYPE_ICON) as rideTypeChoice (rideTypeChoice)}
                                            <li>
                                                <button class="flex items-start justify-between w-full mx-auto p-2 text-left rounded-xs cursor-pointer"
                                                        class:text-white={rideType === rideTypeChoice}
                                                        class:bg-black={rideType === rideTypeChoice}
                                                        class:hover:bg-black={rideType === rideTypeChoice}
                                                        class:hover:bg-gray-100={rideType !== rideTypeChoice}
                                                        onclick={handleRideTypeChange.bind(null, rideTypeChoice)}>
                                                <span class="flex items-start justify-start">
                                                    <span class="h-5">
                                                        <Icon class="h-4 w-4 me-1"
                                                              icon={RIDE_TYPE_ICON[rideTypeChoice]}/>
                                                    </span>
                                                    <span class="text-sm">
                                                        <span class="font-medium">
                                                            {I18n.t(`js.ride.enums.ride_type.${rideTypeChoice}`)}
                                                        </span>
                                                    </span>
                                                </span>
                                                    <span>
                                                    {#if rideType === rideTypeChoice}
                                                        <svg fill="#fff"
                                                             width="16px"
                                                             height="22px"
                                                             viewBox="0 0 1920 1920"
                                                             xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M1743.858 267.012 710.747 1300.124 176.005 765.382 0 941.387l710.747 710.871 1209.24-1209.116z"
                                                                  fill-rule="evenodd"/>
                                                        </svg>
                                                    {/if}
                                                </span>
                                                </button>
                                            </li>
                                        {/each}
                                    </ul>
                                </div>
                            {/if}
                        </div>
                    {/if}

                </div>
            {/if}

            <div class="hidden md:block">
                {@render renderSubmitButton()}
            </div>
        </div>
    </div>
</form>
